import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SellIcon from '@mui/icons-material/Sell';
import VerifiedIcon from '@mui/icons-material/Verified';

import {
    loadCourseProvision,
    updateCourseProvisionParticipants
} from '../../../store/courseService';
import { todayDate } from '../../../utils/dateFunctions';
import Button from '../../formElements/Button';
import StaticField from '../../formElements/StaticField';
import TextAreaField from '../../formElements/TextAreaField';
import LoadingSpinner from '../../ui/LoadingSpinner';

import app from '../../../app.module.css';
import dropdown from '../../../commonStyles/rowExpand.module.css';

const _NOTE_CHAR_LIMIT = 3000;
const _SUBMIT_MESSAGE = 'Participant has been submitted to provision';

const AllActiveProvisionDetails = ({ row, isDisabled, onClose }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [canBeSubmittedToProvision, setCanBeSubmittedToProvision] = useState('');
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
    const [notes, setNotes] = useState('');

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentProvision = useSelector((state) => state.entities.courseService.currentProvision);
    const provisionStatusDetails = useSelector(
        (state) => state.entities.directusService.provisionStatusDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (row && Object.keys(row)?.length !== 0) {
            dispatch(loadCourseProvision(row.id));
            setNotes(row.notes);
        }
    }, [row]);

    useEffect(() => {
        if (currentProvision && Object.keys(currentProvision).length !== 0) {
            setCanBeSubmittedToProvision(true);
            if (currentProvision.participants && currentProvision.participants.length > 0) {
                const existingParticipant = currentProvision.participants.find(
                    (el) => el.participantId === currentParticipant.id
                );
                if (existingParticipant) {
                    const cancelledStatus = provisionStatusDetails.find(
                        (el) => el.name.toLowerCase() === 'cancelled'
                    );
                    if (existingParticipant.statusId !== cancelledStatus?.id)
                        setCanBeSubmittedToProvision(false);
                }
            }
        }
    }, [currentProvision.id]);

    useEffect(() => {
        if (successMessage === _SUBMIT_MESSAGE) {
            setIsSubmitButtonDisabled(false);
            setCanBeSubmittedToProvision(false);
        }
    }, [successMessage]);

    //  FORM SUBMIT
    const onSubmit = (e) => {
        e.preventDefault();
        setIsSubmitButtonDisabled(true);

        let participants = [...currentProvision.participants];
        const submittedStatusId =
            provisionStatusDetails.find((el) => el.name.toLowerCase() === 'submitted')?.id || '';

        if (participants?.length < 1) {
            participants = [
                {
                    participantId: currentParticipant.id,
                    statusId: submittedStatusId,
                    dateSubmitted: todayDate(),
                    notes
                }
            ];
        } else {
            const existingParticipant = currentProvision.participants.find(
                (el) => el.participantId === currentParticipant.id
            );
            if (existingParticipant) {
                const cancelledStatus = provisionStatusDetails.find(
                    (el) => el.name.toLowerCase() === 'cancelled'
                );
                if (existingParticipant.statusId === cancelledStatus?.id)
                    participants = participants.filter((el) => el.id !== existingParticipant.id);
            }
            participants = [
                ...participants,
                {
                    participantId: currentParticipant.id,
                    statusId: submittedStatusId,
                    dateSubmitted: todayDate(),
                    notes
                }
            ];
        }
        const payload = { participants };
        dispatch(updateCourseProvisionParticipants(currentProvision.id, payload, _SUBMIT_MESSAGE));
    };

    return !currentProvision ||
        currentProvision.length < 1 ||
        provisionStatusDetails.length < 1 ||
        canBeSubmittedToProvision === '' ? (
        <LoadingSpinner content="No provision information found" />
    ) : (
        <div>
            <form onSubmit={onSubmit} data-testid="form_start_AllActiveProvisionDetails">
                <div className={`${dropdown.rowDropdown} ${dropdown.redBorder}`}>
                    <div className={app.sectionHeading}>{row.name}</div>
                    <p className={dropdown.content}>{row.description}</p>
                    {row.ofqualNumber && (
                        <p className={dropdown.svgContent}>
                            <VerifiedIcon />
                            <span>{row.ofqualNumber}</span>
                        </p>
                    )}
                    <p className={dropdown.svgContent}>
                        <SellIcon />
                        <span>
                            {`£${row.costPerParticipant} per participant - Payment Method is ${row.paymentMethod}`}
                        </span>
                    </p>
                    <StaticField
                        label="Delivery Information"
                        content={row.deliveryInformation}
                        customClass="info"
                    />
                    <StaticField
                        label="Qualification Required"
                        content={row.qualificationRequired}
                        customClass="info"
                    />
                    <StaticField
                        label="Registration Methods"
                        content={row.registrationMethods}
                        customClass="info"
                    />

                    <TextAreaField
                        id={'notes'}
                        label={'Notes'}
                        disabled={isDisabled}
                        maxLength={_NOTE_CHAR_LIMIT}
                        value={notes}
                        count={
                            notes ? `${notes.length}/${_NOTE_CHAR_LIMIT}` : `0/${_NOTE_CHAR_LIMIT}`
                        }
                        onChange={(e) => setNotes(e.target.value)}
                    />
                    <Button
                        id="activeProvisionDetailsButton"
                        content="Submit Participant To This Provision"
                        disabled={
                            isDisabled ||
                            !canBeSubmittedToProvision ||
                            isSubmitButtonDisabled
                        }
                        icon={true}
                        iconType={<KeyboardArrowRightIcon />}
                    />

                    <div className={dropdown.exit} onClick={onClose}>
                        Close details
                        <CloseIcon />
                    </div>
                </div>
            </form>
        </div>
    );
};

AllActiveProvisionDetails.propTypes = {
    row: PropTypes.object.isRequired,
    isDisabled: PropTypes.bool,
    onClose: PropTypes.func.isRequired
};

export default AllActiveProvisionDetails;
